import { RecorderConfig, RecordingResult, MediaConstraints } from '../types';

export class WebcamRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startTime: number = 0;
  private config: RecorderConfig;
  private isRecording: boolean = false;
  private isPaused: boolean = false;
  private previewElement: HTMLVideoElement | null = null;
  private isInitialized: boolean = false;

  constructor(config: RecorderConfig) {
    this.config = config;
    this.previewElement = config.previewElement || null;
  }

  /**
   * Update configuration while preserving existing stream and preview
   */
  updateConfig(newConfig: RecorderConfig): void {
    const wasInitialized = this.isInitialized;

    // Store current preview element reference
    const currentPreview = this.previewElement;

    // Update config
    this.config = { ...this.config, ...newConfig };

    // If we were initialized and quality changed, we need to reinitialize
    if (wasInitialized && this.hasVideoQualityChanged(newConfig)) {
      this.reinitializeWithNewQuality(currentPreview);
    } else {
      // Just update config without reinitializing
      this.config = { ...this.config, ...newConfig };
    }
  }

  private hasVideoQualityChanged(newConfig: RecorderConfig): boolean {
    return newConfig.videoQuality !== undefined &&
        newConfig.videoQuality !== this.config.videoQuality;
  }

  private async reinitializeWithNewQuality(preservePreview: HTMLVideoElement | null): Promise<void> {
    try {
      // Stop current stream
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
      }

      // Get new stream with updated constraints
      const constraints = this.getMediaConstraints();
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Restore preview if it existed
      if (preservePreview && this.config.showPreview) {
        this.previewElement = preservePreview;
        this.previewElement.srcObject = this.stream;
      }
    } catch (error) {
      console.error('Failed to reinitialize with new quality:', error);
      throw error;
    }
  }

  async initialize(): Promise<void> {
    try {
      const constraints = this.getMediaConstraints();
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      this.isInitialized = true;

      if (this.config.showPreview) {
        this.setupPreview();
      }
    } catch (error) {
      throw new Error(`Failed to access camera: ${(error as Error).message}`);
    }
  }

  /**
   * Set a custom video element for preview instead of creating one
   */
  setPreviewElement(element: HTMLVideoElement): void {
    this.previewElement = element;
    if (this.stream && this.config.showPreview) {
      this.setupPreview();
    }
  }
  private getMediaConstraints(): MediaConstraints {
    const quality = this.config.videoQuality || 'medium';

    const qualitySettings = {
      low: { width: 640, height: 480, frameRate: 15 },
      medium: { width: 1280, height: 720, frameRate: 30 },
      high: { width: 1920, height: 1080, frameRate: 30 },
      auto: { width: 1280, height: 720, frameRate: 30 }
    };

    const settings = qualitySettings[quality];

    return {
      video: {
        width: { ideal: settings.width, max: settings.width },
        height: { ideal: settings.height, max: settings.height },
        frameRate: { ideal: settings.frameRate, max: settings.frameRate }
      },
      audio: this.config.audioEnabled !== false
    };
  }

  private setupPreview(): void {
    let preview = this.previewElement;

    // Use provided element or create a new one
    if (!preview) {
      preview = document.getElementById('video-transcoder-preview') as HTMLVideoElement;
    }

    if (!preview) {
      preview = document.createElement('video');
      preview.id = 'video-transcoder-preview';

      // Apply custom styles if provided, otherwise use defaults
      this.applyPreviewStyles(preview);

      // Only append to body if we created the element
      document.body.appendChild(preview);
    }

    // Configure video element properties
    preview.autoplay = true;
    preview.muted = true;
    preview.playsInline = true;

    // Set the stream
    if (this.stream) {
      preview.srcObject = this.stream;
    }

    // Store reference for cleanup
    this.previewElement = preview;
  }

  private applyPreviewStyles(element: HTMLVideoElement): void {
    if (this.config.customStyles) {
      Object.assign(element.style, this.config.customStyles);
    } else {
      // Default styles
      element.style.width = '100%';
      element.style.maxWidth = '400px';
      element.style.borderRadius = '8px';
      element.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
    }
  }

  async startRecording(): Promise<void> {
    if (!this.stream) {
      throw new Error('Camera not initialized. Call initialize() first.');
    }

    if (this.isRecording) {
      throw new Error('Recording already in progress');
    }

    this.chunks = [];
    this.startTime = Date.now();

    const options: MediaRecorderOptions = {
      mimeType: this.getSupportedMimeType()
    };

    this.mediaRecorder = new MediaRecorder(this.stream, options);

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    this.mediaRecorder.start(1000); // Collect data every second
    this.isRecording = true;
    this.isPaused = false;

    // Auto-stop after max duration
    if (this.config.maxDuration) {
      setTimeout(() => {
        if (this.isRecording && !this.isPaused) {
          this.stopRecording();
        }
      }, this.config.maxDuration * 1000);
    }
  }

  pauseRecording(): void {
    if (this.mediaRecorder && this.isRecording && !this.isPaused) {
      this.mediaRecorder.pause();
      this.isPaused = true;
    }
  }

  resumeRecording(): void {
    if (this.mediaRecorder && this.isRecording && this.isPaused) {
      this.mediaRecorder.resume();
      this.isPaused = false;
    }
  }

  async stopRecording(): Promise<RecordingResult> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || !this.isRecording) {
        reject(new Error('No recording in progress'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const duration = (Date.now() - this.startTime) / 1000;
        const blob = new Blob(this.chunks, {
          type: this.getSupportedMimeType()
        });

        this.isRecording = false;
        this.isPaused = false;

        resolve({
          blob,
          duration,
          size: blob.size,
          mimeType: blob.type
        });
      };

      this.mediaRecorder.stop();
    });
  }

  private getSupportedMimeType(): string {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm';
  }

  cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.mediaRecorder) {
      this.mediaRecorder = null;
    }

    // Only remove preview if we created it (not provided by user)
    if (this.previewElement && this.previewElement.id === 'video-transcoder-preview') {
      this.previewElement.remove();
    }
    this.previewElement = null;

    this.chunks = [];
    this.isRecording = false;
    this.isPaused = false;
    this.isInitialized = false;
  }

  getRecordingState(): {
    isRecording: boolean;
    isPaused: boolean;
    duration: number;
  } {
    return {
      isRecording: this.isRecording,
      isPaused: this.isPaused,
      duration: this.isRecording ? (Date.now() - this.startTime) / 1000 : 0
    };
  }
}
